/**
 * One policy's acknowledgement record.
 *
 * This is the page a surveyor is shown. It has to answer, without
 * interpretation, who was asked, who confirmed, and when — so completed rows
 * are listed with their dates rather than collapsed into a percentage, and the
 * version number is on the page because acknowledging a superseded version is
 * not the same as acknowledging this one.
 */

import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BackLink } from '../../shared/BackLink';
import { useAckReport, ackIdFor, exportAckCsv, saveTextFile, useWaiveAcknowledgement, type AckReportRow } from './useAckReport';
import { formatDate } from '../policies/policyTable';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './Admin.module.css';

type Filter = 'all' | 'outstanding' | 'completed';

/**
 * Settled: nothing is owed. Acknowledged and waived are decisions somebody
 * made; CANCELLED is a requirement that was closed because the version stopped
 * being the one in force, or the policy was retired. It is owed by nobody, so
 * it does not belong in "not yet acknowledged" — and it is evidence of
 * nothing, so it must never be shown as an attestation.
 */
function isDone(row: AckReportRow): boolean {
  return row.status === 'acknowledged' || row.status === 'waived' || row.status === 'cancelled';
}

/**
 * WHAT THE PILL SAYS, AND WHO OBSERVED IT.
 *
 * "Acknowledged" is a claim that StandingDocs watched this person open the
 * policy and confirm they had read it. For a requirement satisfied by the
 * attestation import that claim is false: another system watched it, we were
 * given the record, and the date shown is that system's. The provenance was
 * always in the CSV export and never on the screen, which is the copy a
 * surveyor is shown.
 */
export function statusWords(row: { status: string; source?: string }): string {
  if (row.source === 'legacy_import') return 'Imported acknowledgement';
  return row.status === 'acknowledged' ? 'Acknowledged'
    : row.status === 'waived' ? 'Waived'
      : row.status === 'cancelled' ? 'No longer required'
        : row.status === 'overdue' ? 'Overdue'
          : 'Pending';
}

/**
 * The tone class for a status.
 *
 * `?? ''` because `noUncheckedIndexedAccess` types every CSS-module lookup as
 * possibly undefined — which is honest, since a renamed class would silently
 * become one.
 */
function statusTone(status: string): string {
  if (status === 'acknowledged') return styles.ok ?? '';
  if (status === 'waived' || status === 'cancelled') return styles.neutral ?? '';
  if (status === 'overdue') return styles.danger ?? '';
  return styles.warn ?? '';
}

export function AckReportPage() {
  const [params] = useSearchParams();
  const { policyId } = useParams<{ policyId: string }>();
  /**
   * `?version=` asks for one version's evidence. Absent, the report is of the
   * version in force, which is what every existing link means.
   */
  const askedVersion = params.get('version');
  const { data, isLoading, error } = useAckReport(policyId, askedVersion);
  const [filter, setFilter] = useState<Filter>('outstanding');
  const waive = useWaiveAcknowledgement(policyId);
  const [waiving, setWaiving] = useState<AckReportRow | null>(null);
  const [reason, setReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function doExport() {
    if (!policyId) return;
    setExporting(true); setExportError(null);
    try {
      const file = await exportAckCsv(policyId);
      saveTextFile(file.filename, file.csv);
      setNotice(`Exported ${file.filename}.`);
    } catch (e) {
      setExportError(isCallFailure(e) ? e.message : 'The export could not be made.');
    } finally {
      setExporting(false);
    }
  }

  function doWaive() {
    if (!waiving || !data) return;
    const acknowledgementId = ackIdFor(waiving.userId, data.versionId);
    if (!acknowledgementId || !reason.trim()) return;
    waive.mutate({ acknowledgementId, reason: reason.trim() }, {
      onSuccess: () => { setNotice(`Waived for ${waiving.userName}.`); setWaiving(null); setReason(''); },
    });
  }

  const rows = useMemo(() => {
    const all = data?.acknowledgements ?? [];
    if (filter === 'completed') return all.filter(isDone);
    if (filter === 'outstanding') return all.filter((r) => !isDone(r));
    return all;
  }, [data, filter]);

  if (isLoading) return <Spinner label="Loading acknowledgement report" />;

  if (error) {
    return (
      <PageState
        title="Could not load this report"
        tone="alert"
        action={{ to: '/admin/acknowledgements', label: 'Back to acknowledgements' }}
      >
        {isCallFailure(error) ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  if (!data) {
    return (
      <PageState
        title="Report not available"
        action={{ to: '/admin/acknowledgements', label: 'Back to acknowledgements' }}
      >
        No acknowledgement record exists for that policy.
      </PageState>
    );
  }

  const { stats } = data;

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          <p className={styles.eyebrow}>
            {data.policyNumber}
            {data.versionNumber != null && ` · v${data.versionNumber}`}
          </p>
          <div className={styles.titleLine}>
            <BackLink to="/admin/acknowledgements" label="Back to acknowledgements" />
            <h1 className={styles.title}>{data.policyTitle}</h1>
          </div>
          <p className={styles.subtitle}>
            <Link to={`/policy/${data.policyId}`} className={own.link}>
              View the policy
            </Link>
          </p>
        </div>
      </header>

      <div className={styles.body}>
        {notice && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{notice}</div>}
        {(exportError || waive.error) && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            {exportError ?? (isCallFailure(waive.error) ? waive.error.message : 'That could not be done.')}
          </div>
        )}
        <div className={styles.actions} style={{ marginBottom: 'var(--sp-4)' }}>
          <button type="button" className={styles.btn} onClick={() => void doExport()} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        <div className={own.tiles}>
          <div className={own.tile}>
            <div className={own.tileKey}>Asked</div>
            <div className={`${own.tileValue} tnum`}>{stats.total}</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileKey}>Acknowledged</div>
            <div className={`${own.tileValue} tnum`}>{stats.completed}</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileKey}>Pending</div>
            <div className={`${own.tileValue} tnum`}>{stats.pending}</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileKey}>Overdue</div>
            <div className={`${own.tileValue} tnum ${stats.overdue ? own.danger : ''}`}>
              {stats.overdue}
            </div>
          </div>
          <div className={own.tile}>
            <div className={own.tileKey}>Completion</div>
            <div className={`${own.tileValue} tnum`}>{Math.round(stats.completionRate)}%</div>
          </div>
        </div>

        {/* A real radio group. Tabs would need arrow-key handling and a
            tablist role to be equivalent, and these are a filter rather than
            a change of view. */}
        <fieldset
          style={{ border: 0, padding: 0, margin: '0 0 var(--sp-4)' }}
        >
          <legend className="vh">Show which people</legend>
          <div className={styles.chips}>
            {(['outstanding', 'completed', 'all'] as Filter[]).map((f) => (
              <label key={f} className={styles.chip} style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="ack-filter"
                  value={f}
                  checked={filter === f}
                  onChange={() => setFilter(f)}
                  style={{ marginRight: 6, accentColor: 'var(--accent)' }}
                />
                {f === 'outstanding' ? 'Not yet acknowledged'
                  : f === 'completed' ? 'Acknowledged'
                  : 'Everyone asked'}
              </label>
            ))}
          </div>
        </fieldset>

        <div className={own.count} role="status" aria-live="polite" style={{ paddingBottom: 'var(--sp-3)' }}>
          <b>{rows.length}</b> {rows.length === 1 ? 'person' : 'people'}
        </div>

        <div className={own.scroller}>
          <table className={own.table}>
            <caption>
              Acknowledgement record for {data.policyTitle}
              {data.versionNumber != null && `, version ${data.versionNumber}`}
            </caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Status</th>
                <th scope="col">Acknowledged</th>
                <th scope="col">Due</th>
                <th scope="col"><span className="vh">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId}>
                  <td style={{ fontWeight: 500 }}>{r.userSortName || r.userName}</td>
                  <td style={{ color: 'var(--ink-2)' }}>{r.userEmail}</td>
                  <td>
                    <span className={`${own.pill} ${statusTone(r.status)}`}>{statusWords(r)}</span>
                  </td>
                  <td className="tnum">
                    {r.acknowledgedAt ? formatDate(r.acknowledgedAt)
                      : <span className={styles.absent}>&mdash;</span>}
                    {r.source === 'legacy_import' && (
                      <div className={styles.itemMeta} style={{ marginTop: 2 }}>
                        {r.legacyNote || 'Attested in a previous system, before StandingDocs; imported, not collected here.'}
                      </div>
                    )}
                  </td>
                  <td className="tnum">
                    {r.dueDate ? formatDate(r.dueDate)
                      : <span className={styles.absent}>&mdash;</span>}
                  </td>
                  <td>
                    {!isDone(r) && ackIdFor(r.userId, data.versionId) && (
                      <button type="button" className={styles.btn} onClick={() => { setWaiving(r); setReason(''); setNotice(null); }}
                        aria-label={`Waive for ${r.userName}`} disabled={waive.isPending}>
                        Waive…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(data.legacyTotal ?? 0) > 0 && (
          <section aria-labelledby="legacy-title" style={{ marginTop: 'var(--sp-6)' }}>
            <h2 className={styles.sectionTitle} id="legacy-title">
              Before StandingDocs
              <span className={own.countSuffix}> · {data.legacyTotal!.toLocaleString('en-US')}</span>
            </h2>
            <p className={styles.itemMeta} style={{ maxWidth: '82ch', margin: '0 0 var(--sp-3)' }}>
              Attestations to this policy kept by the system this organization used before, and imported. They were not collected here.
              They cover whatever version each person attested to, and include people who no longer have an account. They are in the export too.
            </p>
            <div className={own.scroller}>
              <table className={own.table}>
                <caption>Imported attestations to {data.policyTitle}</caption>
                <thead>
                  <tr><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Version</th><th scope="col">Attested</th><th scope="col">From</th><th scope="col">Here today</th></tr>
                </thead>
                <tbody>
                  {(data.legacy ?? []).map((l) => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 500 }}>{l.name}{l.employeeId && <div className={styles.itemMeta}>Employee {l.employeeId}</div>}</td>
                      <td style={{ color: 'var(--ink-2)' }}>{l.email ?? <span className={styles.absent}>&mdash;</span>}</td>
                      <td className="tnum">{l.versionLabel ?? <span className={styles.absent}>not given</span>}</td>
                      <td className="tnum" style={{ whiteSpace: 'nowrap' }}>{l.attestedAt ? formatDate(l.attestedAt.slice(0, 10)) : ''}</td>
                      <td>{l.sourceSystem ?? 'a previous system'}{l.countedTowardCurrent && <div className={styles.itemMeta}>Counted toward the current version</div>}</td>
                      <td>{l.memberToday ? 'Member' : <span style={{ color: 'var(--muted)' }}>No longer a member</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(data.legacyTotal ?? 0) > (data.legacy?.length ?? 0) && <p className={own.hint}>Showing the first {(data.legacy?.length ?? 0).toLocaleString('en-US')} of {data.legacyTotal!.toLocaleString('en-US')}. The export has them all.</p>}
          </section>
        )}

        {waiving && (
          <form
            onSubmit={(e) => { e.preventDefault(); doWaive(); }}
            aria-labelledby="waive-title"
            style={{ marginTop: 'var(--sp-4)', padding: 'var(--sp-4)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', maxWidth: '62ch' }}
          >
            <h2 className={styles.sectionTitle} id="waive-title">Waive for {waiving.userName}</h2>
            <p className={styles.absent} style={{ marginTop: 0 }}>
              Excuses this person from acknowledging this version. The reason is kept on the record a surveyor sees.
            </p>
            <label className={own.label} htmlFor="waive-reason">Reason</label>
            <textarea id="waive-reason" className={own.input} rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 1000))} required disabled={waive.isPending} />
            <div className={styles.actions} style={{ marginTop: 'var(--sp-3)' }}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={waive.isPending || !reason.trim()}>{waive.isPending ? 'Waiving…' : 'Waive'}</button>
              <button type="button" className={styles.btn} onClick={() => setWaiving(null)}>Cancel</button>
            </div>
          </form>
        )}

        {rows.length === 0 && (
          <p className={styles.state}>
            {filter === 'outstanding'
              ? 'Everyone asked has acknowledged this policy.'
              : 'Nobody matches that filter.'}
          </p>
        )}

        {data.hasMore && (
          <p className={styles.absent} style={{ marginTop: 'var(--sp-4)' }}>
            Showing the first 500 people. Export the CSV for everyone.
          </p>
        )}
      </div>
    </>
  );
}
